import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppSelector } from "@/store/store";
import { BranchService } from "@/services/Branch/BranchSevice";
import { SiteService } from "@/services/Site/SiteServive";
import type { branch } from "@/interface/branch.interface";
import type { site } from "@/interface/site.interface";
import SearchSelect, { type Option } from "@/components/common/SearchSelect";
import { ThrowError } from "@/api/callApi";

export interface UseBranchSiteFilterOptions {
  /** Auto select first branch when branches load (default: true) */
  autoSelectFirstBranch?: boolean;
  /** Auto select first site when branch is selected (default: true) */
  autoSelectFirstSite?: boolean;
  /** Callback fired when effective branch or site changes */
  onChange?: (params: { branchId: string; siteId: string }) => void;
}

// ── Stable Top-Level Component to prevent unmounting on state updates ────────
export interface BranchSiteSelectorsProps {
  isHQ?: boolean;
  branchOptions: Option[];
  siteOptions: Option[];
  selectedBranchId: string;
  selectedSiteId: string;
  onBranchChange: (branchId: string) => void;
  onSiteChange: (siteId: string) => void;
  onSearchBranch?: (searchTerm: string) => void;
  onSearchSite?: (searchTerm: string) => void;
  isLoadingBranches?: boolean;
  isLoadingSites?: boolean;
  className?: string;
  branchClassName?: string;
  siteClassName?: string;
}

export const BranchSiteSelectors: React.FC<BranchSiteSelectorsProps> = React.memo(
  ({
    isHQ = true,
    branchOptions,
    siteOptions,
    selectedBranchId,
    selectedSiteId,
    onBranchChange,
    onSiteChange,
    onSearchBranch,
    onSearchSite,
    isLoadingBranches = false,
    isLoadingSites = false,
    className = "",
    branchClassName = "w-full sm:w-52 min-w-[100px]",
    siteClassName = "w-full sm:w-52 min-w-[100px]",
  }) => {
    if (!isHQ) return null;

    return (
      <div className={`flex items-center gap-4 flex-wrap sm:flex-nowrap ${className}`}>
        {/* Branch Select */}
        <div className={branchClassName}>
          <SearchSelect
            placeholder="Select Branch..."
            options={branchOptions}
            value={selectedBranchId}
            onChange={onBranchChange}
            onSearch={onSearchBranch}
            isLoading={isLoadingBranches}
            allowClear={false}
            className="w-full"
          />
        </div>

        {/* Site Select */}
        <div className={siteClassName}>
          <SearchSelect
            placeholder="Select Site..."
            options={siteOptions}
            value={selectedSiteId}
            onChange={onSiteChange}
            onSearch={onSearchSite}
            isLoading={isLoadingSites}
            disabled={!selectedBranchId || siteOptions.length === 0}
            allowClear={false}
            className="w-full"
          />
        </div>
      </div>
    );
  }
);

BranchSiteSelectors.displayName = "BranchSiteSelectors";

export const useBranchSiteFilter = (options: UseBranchSiteFilterOptions = {}) => {
  const { autoSelectFirstBranch = true, autoSelectFirstSite = true, onChange } = options;

  const user = useAppSelector((state) => state.user.user);

  // ── Determine if logged-in user is Level 0 (Super Admin / HQ) ──────────────
  const isHQ = useMemo(() => {
    if (!user) return false;
    const level = Number(user.designation?.level);
    return level === 0;
  }, [user]);

  const [branches, setBranches] = useState<branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [isLoadingBranches, setIsLoadingBranches] = useState<boolean>(false);

  const [allSites, setAllSites] = useState<site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [isLoadingSites, setIsLoadingSites] = useState<boolean>(false);

  // ── Fetch branches & sites when user is Level 0 ────────────────────────────
  const fetchBranchAndSiteData = useCallback(async () => {
    if (!isHQ) return;
    setIsLoadingBranches(true);
    setIsLoadingSites(true);
    try {
      const [branchRes, siteRes] = await Promise.all([
        BranchService.getBranch("pageSize=100"),
        SiteService.getSite("pageSize=300"),
      ]);

      let bList: branch[] = [];
      let sList: site[] = [];
      let initialBranchId = "";
      let initialSiteId = "";

      if (branchRes?.status && branchRes?.data?.branch) {
        bList = branchRes.data.branch;
        if (autoSelectFirstBranch && bList.length > 0) {
          initialBranchId = bList[0]?.id || "";
        }
      }

      if (siteRes?.status && siteRes?.data?.site) {
        sList = siteRes.data.site;
        if (initialBranchId && autoSelectFirstSite) {
          const matched = sList.find(
            (s) =>
              s?.branchId === initialBranchId ||
              (s?.branch && s?.branch?.id === initialBranchId)
          );
          if (matched) {
            initialSiteId = matched.id || "";
          } else if (sList.length > 0) {
            initialSiteId = sList[0]?.id || "";
          }
        }
      }

      setBranches(bList);
      setAllSites(sList);
      if (initialBranchId) setSelectedBranchId(initialBranchId);
      if (initialSiteId) setSelectedSiteId(initialSiteId);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranches(false);
      setIsLoadingSites(false);
    }
  }, [isHQ, autoSelectFirstBranch, autoSelectFirstSite]);

  useEffect(() => {
    fetchBranchAndSiteData();
  }, [fetchBranchAndSiteData]);

  // ── Available sites filtered by selected branch ────────────────────────────
  const availableSites = useMemo(() => {
    if (!selectedBranchId) return allSites;
    return allSites.filter(
      (s) =>
        s.branchId === selectedBranchId ||
        (s.branch && s.branch.id === selectedBranchId)
    );
  }, [allSites, selectedBranchId]);

  // ── Handle Branch Change ───────────────────────────────────────────────────
  const handleBranchChange = useCallback(
    (branchId: string) => {
      setSelectedBranchId(branchId);

      // Filter sites for this newly selected branch
      const matchedSites = allSites.filter(
        (s) =>
          s.branchId === branchId ||
          (s.branch && s.branch.id === branchId)
      );

      let newSiteId = "";
      if (autoSelectFirstSite && matchedSites.length > 0) {
        newSiteId = matchedSites[0]?.id || "";
      }
      setSelectedSiteId(newSiteId);

      onChange?.({ branchId, siteId: newSiteId });
    },
    [allSites, autoSelectFirstSite, onChange]
  );

  // ── Remote API Search for Branch ──────────────────────────────────────────
  const handleSearchBranch = useCallback(async (searchTerm: string) => {
    setIsLoadingBranches(true);
    try {
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const branchRes = await BranchService.getBranch(`pageSize=50${searchParam}`);
      if (branchRes?.status && branchRes?.data?.branch) {
        setBranches(branchRes.data.branch);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranches(false);
    }
  }, []);

  // ── Remote API Search for Site ────────────────────────────────────────────
  const handleSearchSite = useCallback(
    async (searchTerm: string) => {
      setIsLoadingSites(true);
      try {
        const branchParam = selectedBranchId ? `&branchId=${selectedBranchId}` : "";
        const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
        const siteRes = await SiteService.getSite(`pageSize=100${branchParam}${searchParam}`);
        if (siteRes?.status && siteRes?.data?.site) {
          setAllSites((prev) => {
            const newSites = siteRes.data.site || [];
            const map = new Map<string, site>();
            prev.forEach((s) => {
              if (s?.id) map.set(s.id, s);
            });
            newSites.forEach((s) => {
              if (s?.id) map.set(s.id, s);
            });
            return Array.from(map.values());
          });
        }
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsLoadingSites(false);
      }
    },
    [selectedBranchId]
  );

  // ── Handle Site Change ─────────────────────────────────────────────────────
  const handleSiteChange = useCallback(
    (siteId: string) => {
      setSelectedSiteId(siteId);
      onChange?.({ branchId: selectedBranchId, siteId });
    },
    [selectedBranchId, onChange]
  );

  // ── Format Options for SearchSelect ───────────────────────────────────────
  const branchOptions = useMemo<Option[]>(() => {
    return branches.map((b) => ({
      value: b.id || "",
      label: b.name || "Unnamed Branch",
    }));
  }, [branches]);

  const siteOptions = useMemo<Option[]>(() => {
    return availableSites.map((s) => ({
      value: s.id || "",
      label: s.name || "Unnamed Site",
    }));
  }, [availableSites]);

  // ── Effective Branch and Site IDs for queries ──────────────────────────────
  // If Level 0 (HQ): use selected Branch and Site IDs.
  // If Non-Level 0: only send siteId (branchId remains empty string).
  const effectiveBranchId = isHQ ? selectedBranchId : "";
  const effectiveSiteId = isHQ ? selectedSiteId : user?.site?.id || "";

  // ── Memoized Selectors Node to directly pass into additionalFilters ────────
  const branchSiteSelectorsNode = useMemo(() => {
    if (!isHQ) return null;
    return (
      <BranchSiteSelectors
        isHQ={isHQ}
        branchOptions={branchOptions}
        siteOptions={siteOptions}
        selectedBranchId={selectedBranchId}
        selectedSiteId={selectedSiteId}
        onBranchChange={handleBranchChange}
        onSiteChange={handleSiteChange}
        onSearchBranch={handleSearchBranch}
        onSearchSite={handleSearchSite}
        isLoadingBranches={isLoadingBranches}
        isLoadingSites={isLoadingSites}
      />
    );
  }, [
    isHQ,
    branchOptions,
    siteOptions,
    selectedBranchId,
    selectedSiteId,
    handleBranchChange,
    handleSiteChange,
    handleSearchBranch,
    handleSearchSite,
    isLoadingBranches,
    isLoadingSites,
  ]);

  return {
    isHQ,
    branches,
    allSites,
    availableSites,
    branchOptions,
    siteOptions,
    selectedBranchId,
    selectedSiteId,
    effectiveBranchId,
    effectiveSiteId,
    isLoadingBranches,
    isLoadingSites,
    setSelectedBranchId,
    setSelectedSiteId,
    handleBranchChange,
    handleSiteChange,
    handleSearchBranch,
    handleSearchSite,
    refetch: fetchBranchAndSiteData,
    branchSiteSelectorsNode,
    BranchSiteSelectors,
  };
};
